import { track } from './track'
import type { FunnelStep } from './funnel'

export type { FunnelStep }

export type PageType =
  | 'home'
  | 'about'
  | 'pricing'
  | 'solution'
  | 'solution_feature'
  | 'tool'
  | 'resource_index'
  | 'resource'
  | 'blog_index'
  | 'blog_article'
  | 'links'

export type ToolName =
  | 'compliance-audit'
  | 'ppms-audit'
  | 'security-audit-alerts'
  | 'solution-configurator'

// --- Common layer ---
export const trackScrollDepth = (p: { pageType: PageType; percent: number }) =>
  track('scroll_depth', { page_type: p.pageType, percent: p.percent })

export const trackSectionView = (p: { pageType: PageType; section: string }) =>
  track('section_view', { page_type: p.pageType, section: p.section })

export const trackOutboundClick = (p: { pageType: PageType; destination: string }) =>
  track('outbound_click', { page_type: p.pageType, destination: p.destination })

export const trackNavClick = (p: { destination: string; source: 'header' | 'footer' }) =>
  track('nav_click', { destination: p.destination, source: p.source })

// --- CTAs ---
export const trackCtaClick = (p: {
  ctaId: string
  ctaType: string
  pageType: PageType
  section: string
  destination: string
}) =>
  track('cta_click', {
    cta_id: p.ctaId,
    cta_type: p.ctaType,
    page_type: p.pageType,
    section: p.section,
    destination: p.destination,
  })

export const trackBookDemo = (p: { pageType: PageType; section: string }) =>
  track('book_demo_click', { page_type: p.pageType, section: p.section })

// --- Trial-form funnel ---
// One global funnel across every "Essayer gratuitement" entry point:
// trial_click → trial_form_start → trial_submit. `source` (hero, header,
// footer, faq…) rides along as event data so the funnel can be broken down by
// where the click came from. Fires wherever the trial form is used; `source`
// is optional and simply omitted when a caller doesn't set one.
export const trackTrialClick = (p?: { source?: string }) =>
  track('trial_click', { source: p?.source })

export const trackTrialFormStart = (p?: { source?: string }) =>
  track('trial_form_start', { source: p?.source })

export const trackTrialSubmit = (p?: { source?: string }) =>
  track('trial_submit', { source: p?.source })

// --- Conversions (key events) ---
export const trackLead = (p: { tool: ToolName }) =>
  track('generate_lead', { tool: p.tool })

export const trackLeadMagnetClick = (p: { magnetId: string; pageType: PageType }) =>
  track('lead_magnet_click', { magnet_id: p.magnetId, page_type: p.pageType })

export const trackResourceDownload = (p: { magnetId: string }) =>
  track('resource_download', { magnet_id: p.magnetId })

export const trackResourceToToolClick = (p: { magnetId: string; tool: string }) =>
  track('resource_to_tool_click', { magnet_id: p.magnetId, tool: p.tool })

// --- Tool funnel ---
export const trackToolStart = (p: { tool: ToolName }) =>
  track('tool_start', { tool: p.tool })

export const trackToolQuestionAnswered = (p: { tool: ToolName; questionIndex: number }) =>
  track('tool_question_answered', { tool: p.tool, question_index: p.questionIndex })

export const trackToolGateReached = (p: { tool: ToolName }) =>
  track('tool_gate_reached', { tool: p.tool })

export const trackToolResultViewed = (p: { tool: ToolName }) =>
  track('tool_result_viewed', { tool: p.tool })

export const trackToolAbandon = (p: {
  tool: ToolName
  step: FunnelStep
  questionIndex: number
}) =>
  track('tool_abandon', {
    tool: p.tool,
    step: p.step,
    question_index: p.questionIndex,
  })

export const trackFormFieldError = (p: { tool: ToolName; field: string }) =>
  track('form_field_error', { tool: p.tool, field: p.field })

export const trackConfiguratorApp = (p: { action: 'added' | 'removed'; app: string }) =>
  track(`configurator_app_${p.action}`, { app: p.app })

// --- Per-page ---
export const trackFaqOpen = (p: { question: string }) =>
  track('faq_open', { question: p.question })

export const trackAppCardClick = (p: { app: string }) =>
  track('app_card_click', { app: p.app })

export const trackShowcaseView = (p: { pageType: PageType }) =>
  track('showcase_view', { page_type: p.pageType })

export const trackFeatureCtaClick = (p: { feature: string }) =>
  track('feature_cta_click', { feature: p.feature })

export const trackPricingPlanView = (p: { planId: string }) =>
  track('pricing_plan_view', { plan_id: p.planId })

export const trackPricingCtaClick = (p: { planId: string }) =>
  track('pricing_cta_click', { plan_id: p.planId })

export const trackEnterpriseCtaClick = () => track('enterprise_cta_click')

export const trackBillingToggle = (p: { period: string }) =>
  track('billing_toggle', { period: p.period })

export const trackReadDepth = (p: { percent: number }) =>
  track('read_depth', { percent: p.percent })

export const trackReadComplete = () => track('read_complete')

export const trackRelatedClick = (p: { destination: string }) =>
  track('related_click', { destination: p.destination })

export const trackBlogCtaClick = () => track('blog_cta_click')

export const trackListItemClick = (p: { destination: string }) =>
  track('list_item_click', { destination: p.destination })

export const trackAboutCtaClick = () => track('about_cta_click')

export const trackEventCardClick = () => track('event_card_click')
